class ClientEditFormController {

    constructor(
        $stateParams,
        ClientEditFormService,
        UserHelper
    ){
        'ngInject';

        this.$stateParams = $stateParams;
        this.ClientEditFormService = ClientEditFormService;
        this.UserHelper = UserHelper;
    }

    $onInit() {
        this.userList = this.relatedModels.User;
        this.selectedFieldOperationsDirector = this.getFieldOperationsDirector(this.client.field_operations_director_id);
        this.selectedAccountDevelopmentDirector = this.getAccountDevelopmentDirector(this.client.account_development_director_id);
        this.selectedGroup = this.getGroup(this.client.group_ids);

        this.isErrorVisible = false;
        this.errorMessage = '';
        this.showSaveSuccess = false;

        this.industryCodeMap = this.ClientEditFormService.industryCodeMap;
        this.industryCodeIdMap = this.ClientEditFormService.industryCodeIdMap;

        this.industryCode = this.client.industry_code ? this.client.industry_code.code : undefined;
        this.industryCodeErrorMessage = 'Please enter a valid NAICS Industry Code.';

        this.setMonthSelections();

        // need to have users be able to select a "null" or empty choice
        // Start with active reasons from the API
        this.contractTerminationReasons = [{ id: '', name: 'None Set'}, ...this.relatedModels.ClientContractCancelReason];
        
        // If the client has a contract_end_reason that's not in the active list (e.g., inactive COVID),
        // add it to the list so it can be displayed
        if (this.client.contract_end_reason) {
            // Handle both object format (from serializer) and ID format (legacy)
            const currentReason = typeof this.client.contract_end_reason === 'object' 
                ? this.client.contract_end_reason 
                : null;
            const currentReasonId = currentReason ? currentReason.id : this.client.contract_end_reason;
            
            if (currentReasonId) {
                // Convert to number for comparison (IDs might be strings or numbers)
                const reasonIdNum = typeof currentReasonId === 'string' ? parseInt(currentReasonId, 10) : currentReasonId;
                const isInList = this.contractTerminationReasons.some(reason => {
                    const reasonId = typeof reason.id === 'string' ? parseInt(reason.id, 10) : reason.id;
                    return reasonId === reasonIdNum;
                });
                
                if (!isInList) {
                    // Add the client's current reason even if it's inactive
                    if (currentReason) {
                        this.contractTerminationReasons.push(currentReason);
                    } else {
                        // If we only have an ID, create a placeholder object
                        // This shouldn't happen with the new serializer, but handle it for safety
                        this.contractTerminationReasons.push({ id: currentReasonId, name: 'Unknown Reason' });
                    }
                }
            }
        }
        
        // select the reason that came back from the database
        let reasonId = null;
        if (this.client.contract_end_reason) {
            if (typeof this.client.contract_end_reason === 'object' && this.client.contract_end_reason.id) {
                reasonId = this.client.contract_end_reason.id;
            } else {
                reasonId = this.client.contract_end_reason;
            }
        }
        this.selectedContractTerminationReason = this.getContractTerminationReason(reasonId);

        this.client.users = this.configureClientUsers(this.client.users);

        this.previousClientName = this.client.name;
    }


    /***
     * Grab the Client Contract Cancel reason whose ID is equal to the provided value.
     *
     * @param reasonId     the IDs of the temporary reason being fetched.
     *
     * @returns {temp reason object}
     */
    getContractTerminationReason(reasonId) {
        if (reasonId === null || reasonId === undefined || reasonId === '') {
            return this.contractTerminationReasons[0];
        }
        // Convert to number for comparison (IDs might be strings or numbers)
        const reasonIdNum = typeof reasonId === 'string' ? parseInt(reasonId, 10) : reasonId;
        return this.contractTerminationReasons.filter((g) => {
            const gId = typeof g.id === 'string' ? parseInt(g.id, 10) : g.id;
            return gId === reasonIdNum;
        }).pop() || this.contractTerminationReasons[0];
    }

    /***
     * Assigns the selected Contract Cancel Reason to the display property as well as
     * assigning its ID to the current client.
     *
     * @param item      a temp reason object
     */
    setContractTerminationReason(item) {
        this.selectedContractTerminationReason = item;
        this.client.contract_end_reason = item.id;
    }


    configureClientUsers(users) {
        return users.map((u) => {
            u.canViewAllClients = (this.UserHelper.hasPermission(u, this.UserHelper.viewAllClientsCode, true)
                || this.UserHelper.hasPermission(u, this.UserHelper.viewPTClientsCode, true));
            u.clientSpecific = !(this.UserHelper.hasPermission(u, this.UserHelper.viewAllClientsCode, true)
                || this.UserHelper.hasPermission(u, this.UserHelper.viewPTClientsCode, true));
           if (u.canViewAllClients) {
               u.customClass = 'unselectable';
               u.cantRemove = true;
           }
           return u;
        });
    }

    hideError() {
        this.isErrorVisible = false;
        this.errorMessage = '';
    }


    /***
     * Grab the FieldOperationsDirector whose ID is equal to the provided value.
     *
     * @param fieldOperationsDirectorId     the ID of the field operations director being fetched.
     *
     * @returns {FieldOperationsDirector object}
     */
    getFieldOperationsDirector(fieldOperationsDirectorId) {
        return this.userList.filter((u) => u.id === fieldOperationsDirectorId).pop();
    }

    /***
     * Grab the AccountDevelopmentDirector whose ID is equal to the provided value.
     *
     * @param accountDevelopmentDirectorId     the ID of the field operations director being fetched.
     *
     * @returns {AccountDevelopmentDirector object}
     */
    getAccountDevelopmentDirector(accountDevelopmentDirectorId) {
        return this.userList.filter((u) => u.id === accountDevelopmentDirectorId).pop();
    }

    /***
     * Grab the Group whose ID is equal to the provided value.
     *
     * @param groupIds     the IDs of the group being fetched.
     *
     * @returns {Group object}
     */
    getGroup(groupIds) {
        const groupId = groupIds[0]; // a list of group ids is returned from endpoint
        return this.groupList.filter((g) => g.id === groupId).pop();
    }


    /***
     * Assigns the selected FO Director to the display property as well as assigning its ID to the current client.
     *
     * @param item      a FieldOperationsDirector object
     */
    setFieldOperationsDirector(item) {
        this.selectedFieldOperationsDirector = item;
        this.client.field_operations_director_id = item.id;
    }

    /***
     * Assigns the selected Account Development Director to the display property
     * as well as assigning its ID to the current client.
     *
     * @param item      a AccountDevelopmentDirector object
     */
    setAccountDevelopmentDirector(item) {
        this.selectedAccountDevelopmentDirector = item;
        this.client.account_development_director_id = item.id;
    }

    /***
     * Assigns the selected Group to the display property as well as assigning its ID to the current client.
     *
     * @param item      a group object
     */
    setGroup(item) {
        this.selectedGroup = item;
        this.client.group_ids = [item.id];
    }



    updateClientUsers(item, itemIds) {
        const data = {
            user_ids: itemIds
        };
        return this.ClientEditFormService.updateClient(this.client.id, data).then((successResponse) => {
            return this.configureClientUsers(successResponse.data.users);
        })
    }


    getMonthYear(dateAsString) {
        const dateParts = dateAsString.split('-');
        return {'year': parseInt(dateParts[0]), 'month': parseInt(dateParts[1])}
    }

    /***
     * Sets the month and year values for the contract dates
     */
    setMonthSelections() {

        const currentDate = new Date();
        this.month = currentDate.getMonth() + 1;
        this.year = currentDate.getFullYear();

        this.contractStartMonth = 13;
        this.contractStartYear = 'None Set';
        if (this.client.contract_start_date) {
            const startDateParts = this.getMonthYear(this.client.contract_start_date);
            this.contractStartMonth = startDateParts['month'];
            this.contractStartYear = startDateParts['year'];
        }

        this.contractEndMonth = 13;
        this.contractEndYear = 'None Set';
        if (this.client.contract_end_date) {
            const endDateParts = this.getMonthYear(this.client.contract_end_date);
            this.contractEndMonth = endDateParts['month'];
            this.contractEndYear = endDateParts['year'];
        }

    }

    onContractStartDateChange(month, year) {
        this.contractStartMonth = month;
        this.contractStartYear = year;
    }

    onContractEndDateChange(month, year) {
        this.contractEndMonth = month;
        this.contractEndYear = year;
    }

    saveClientSettings() {
        let data = {};
        this.showSaveSuccess = false;

        const industryCode = this.industryCodeMap[this.industryCode];
        if (industryCode === undefined || !industryCode.hasOwnProperty('id')) {
            data['industry_code_id'] = '';
        } else {
            data['industry_code_id'] = industryCode.id;
        }

        /* forcing the new value upon the client object */
        this.client.industry_code_id = data['industry_code_id']
        this.client.industry_code = industryCode

        let contractStartDate = null;
        let contractStartDateString = null;
        if (this.contractStartYear !== 'None Set' && this.contractStartMonth !== 13) {
            contractStartDate = new Date(Date.UTC(this.contractStartYear, this.contractStartMonth - 1, 1));
            contractStartDateString = contractStartDate.toISOString().slice(0, 10);
        }

        let contractEndDate = null;
        let contractEndDateString = null;
        if (this.contractEndYear !== 'None Set' && this.contractEndMonth !== 13) {
            // the months sent are the 'real-life' months, so they're ahead by one month in
            // JS months, which is fine since we want to get the last day of the 'real-life' month,
            // which the 0 provides
            contractEndDate = new Date(Date.UTC(this.contractEndYear, this.contractEndMonth, 0));
            contractEndDateString = contractEndDate.toISOString().slice(0, 10);
        }

        const saveableFields = [
            'name',
            'field_operations_director_id',
            'account_development_director_id',
            'group_ids'
        ];

        for (let field of saveableFields) {
            if (this.client.hasOwnProperty(field)) {
                data[field] = this.client[field] ? this.client[field] : null;
            }
        }
        data['contract_start_date'] = contractStartDateString;
        data['contract_end_date'] = contractEndDateString;
        data['contract_end_reason_id'] = this.selectedContractTerminationReason.id;

        this.ClientEditFormService.updateClient(this.client.id, data)
            .then((successResponse) => {
                this.showSaveSuccess = true;
                if (this.previousClientName !== this.client.name) {
                    this.previousClientName = this.client.name;
                }
                this.updateTree();
            }, (errorResponse) => {
                this.isErrorVisible = true;
                if ('name' in errorResponse.data) {
                    this.errorMessage = errorResponse.data['name'][0];
                } else {
                    this.errorMessage = errorResponse.data
                }
            });
    }

}

export default ClientEditFormController;


// WEBPACK FOOTER //
// ./app/components/client-edit/client-edit-form/client-edit-form-controller.js